<template>
  <!--
    The single mount of the plugin for brands, kept out of the header partials so
    the desktop and mobile triggers share one window. Occupies no space: the
    plugin's window is fixed-position and escapes this element anyway.
  -->
  <div
    v-if="brandAssistantEnabled"
    class="brand-assistant-panel"
  >
    <SearchAi
      v-model:open-ak-drawer="isOpen"
      audience="brand"
      :account-uuid="accountUuid"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { SearchAi } from '@ankorstore/search-ai';
import store from '@monolith/legacy/store';
import { useAgent } from '../composables/use-agent/use-agent';
import { useBrandAssistant } from '../composables/use-agent/use-brand-assistant';
import { useAssistantInset } from '../composables/use-agent/use-assistant-inset';
import { useWarmSuggestions } from '../composables/use-agent/use-warm-suggestions';

const { brandAssistantEnabled } = useAgent();
const { isOpen } = useBrandAssistant();

// Mounted once, so this is the one place that owns the inset. Gated on the
// same flag as the markup: the property it publishes is read by the retailer
// agent's full-screen rule too, so leaving it set here would inset a window
// this feature does not own.
useAssistantInset(brandAssistantEnabled);

// The flag is passed, not assumed: the layout mounts this component for every
// authenticated user and the `v-if` above only guards its markup, so setup runs
// for retailers and outside the back office too.
useWarmSuggestions(brandAssistantEnabled);

/**
 * Whose suggestion set the plugin should listen for (SEL-181).
 *
 * The plugin subscribes to a per-account Mercure topic to hear that a pending
 * set is ready, and being host-agnostic it cannot know the account itself.
 *
 * Empty for a retailer and for a brand record that carries no uuid — the field
 * is optional on `Brand`. The plugin reads an empty value as "no subscription"
 * and falls back to asking again, rather than holding a stream open for an
 * event that cannot arrive.
 */
const accountUuid = computed(() =>
  brandAssistantEnabled.value ? (store.getters['brand']?.uuid ?? '') : ''
);
</script>

<style lang="scss" scoped>
.brand-assistant-panel {
  width: 0;
  height: 0;
  overflow: hidden;
}
</style>
